import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, X, LogIn, Euro, Tag, Sparkles } from 'lucide-react';
import { auth, db } from '../firebase/config';
import { collection, addDoc, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import AuthModal from './AuthModal';
import CheckoutForm from './CheckoutForm.js';
import { usePromotions } from '../hooks/UsePromotion';
import AlertModal from './AlertModal.js';
import { useAlert } from '../hooks/UseAlert.js';
import PriceDisplay from './PriceDisplay.js';

import douceur_cristalline from '../assets/douceur_cristalline.JPG';
import energie_cristalline from '../assets/energie_cristalline.JPG';
import jade_cristalline from '../assets/jade_cristalline.JPG';

const SERVICE_IMAGES = {
  "Douceur cristalline": douceur_cristalline,
  "Énergie cristalline": energie_cristalline,
  "Jade cristalline": jade_cristalline
};

const ServiceDetail = ({ service, category, onBack }) => {
  const navigate = useNavigate();
  const [showBooking, setShowBooking] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [pendingBookingId, setPendingBookingId] = useState(null);
  const PAYMENT_DISABLED_UNTIL = new Date('2026-02-08T23:59:59');
  const { applyPromotion } = usePromotions();
  const { success, error, warning, alertConfig, closeAlert } = useAlert();

  const [bookingData, setBookingData] = useState({
    date: '',
    time: ''
  });
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  const isEclipseaService = category?.title === "Eclipse autour du monde";

  let automaticPromotion = null;

  const allTimeSlots = ["08:00", "08:15", "08:30", "08:45", "09:00", "09:15", "09:30", "09:45", "10:00", "10:15", "10:30", "10:45", "11:00", "11:15", "11:30", "11:45", "12:00", "12:15", "12:30", "12:45", "13:00", "13:15", "13:30", "13:45", "14:00", "14:15", "14:30", "14:45", "15:00", "15:15", "15:30", "15:45", "16:00", "16:15", "16:30", "16:45", "17:00", "17:15", "17:30", "17:45", "18:00"];

  const openingHours = {
    1: null,
    2: { start: "14:00", end: "20:00" },
    3: { start: "11:00", end: "20:00" },
    4: { start: "11:00", end: "20:00" },
    5: { start: "11:00", end: "20:00" },
    6: { start: "10:00", end: "18:00" },
    0: null,
  };

  const handleBackToSite = () => {
    navigate('/');
  };

  const isPaymentEnabled = () => {
    const now = new Date();
    return now > PAYMENT_DISABLED_UNTIL;
  };

  const hasVariableOptions = () => {
    return service.duration && (
      service.duration.includes('-') ||
      service.price.includes('-')
    );
  };

  const getOptionDescription = (duration, category) => {
    const categoryTitle = category?.title;

    if (categoryTitle === "Rituels au coeur des délices") {
      if (duration === "1h" || duration === "1h00") {
        return "Gommage 30min + Massage 30min";
      }
      if (duration === "1h15") {
        return "Gommage 30min + Massage 45min";
      }
    }

    const variableCategories = [
      "Voyage Autour du Monde",
      "Massages Autour des Femmes",
      "Massages Eveil des sens",
      "Massages Énergétiques"
    ];

    if (variableCategories.includes(categoryTitle)) {
      if (duration === "1h" || duration === "1h00") {
        return "Corps uniquement";
      }
      if (duration === "1h30") {
        return "Corps + Visage + Cuir chevelu";
      }
    }

    if (categoryTitle === "Rituels du Corps") {
      if (duration === "30mins") {
        return "Séance courte";
      }
      if (duration === "45mins") {
        return "Séance prolongée";
      }
    }

    return null;
  };

  const getServiceOptions = () => {
    if (!hasVariableOptions()) {
      return [{
        duration: service.duration,
        price: service.price,
        description: getOptionDescription(service.duration, category)
      }];
    }

    const durations = service.duration.split('-');
    const prices = service.price.split('-');

    return durations.map((dur, index) => ({
      duration: dur.trim(),
      price: prices[index]?.trim() || prices[0],
      description: getOptionDescription(dur.trim(), category)
    }));
  };

  const getServiceOptionsWithPromotions = () => {
    const options = getServiceOptions();

    return options.map(option => {

      let promotion = applyPromotion(option.price, category?.title);

      if (!promotion?.hasPromotion && service?.automaticDiscount) {
        const originalPriceNumber = extractAmount(option.price);
        const discountPercent = service.automaticDiscount;

        const discountedPrice = (
          originalPriceNumber * (1 - discountPercent / 100)
        ).toFixed(2);

        promotion = {
          hasPromotion: true,
          originalPrice: `${originalPriceNumber}€`,
          discountedPrice: `${discountedPrice}€`,
          discountPercent
        };
      }

      return {
        ...option,
        promotion
      };
    });
  };

  const serviceOptions = getServiceOptionsWithPromotions();

  useEffect(() => {
    if (serviceOptions.length === 1) {
      setSelectedOption(serviceOptions[0]);
    } else {
      setSelectedOption(null);
    }
    setBookingData({ date: '', time: '' });
  }, [service]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const userDoc = await getDocs(
          query(collection(db, 'users'), where('__name__', '==', currentUser.uid))
        );
        if (!userDoc.empty) {
          setUserData(userDoc.docs[0].data());
        }
      }
    });

    return () => unsubscribe();
  }, []);

  const calculateBlockedSlots = (startTime, duration) => {
    const slots = [];
    const [hours, minutes] = startTime.split(':').map(Number);
    let totalMinutes = hours * 60 + minutes;

    let durationMinutes = 0;
    if (duration.includes('h')) {
      const parts = duration.match(/(\d+)h(\d+)?/);
      if (parts) {
        durationMinutes = parseInt(parts[1]) * 60 + (parts[2] ? parseInt(parts[2]) : 0);
      }
    } else if (duration.includes('mins')) {
      durationMinutes = parseInt(duration);
    }

    const totalDuration = durationMinutes + 15;
    const slotsNeeded = Math.ceil(totalDuration / 15);

    for (let i = 0; i < slotsNeeded; i++) {
      const slotMinutes = totalMinutes + (i * 15);
      const h = Math.floor(slotMinutes / 60);
      const m = slotMinutes % 60;
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
    return slots;
  };

  const fetchBlockedSlots = async (date) => {
    try {
      const bookingsRef = collection(db, 'bookings');
      const q = query(bookingsRef, where('date', '==', date));
      const querySnapshot = await getDocs(q);

      const allBlockedSlots = [];
      querySnapshot.forEach((doc) => {
        const booking = doc.data();
        if (booking.status !== 'pending_payment' && booking.status !== 'cancelled') {
          allBlockedSlots.push(...booking.blockedSlots);
        }
      });

      setBlockedSlots(allBlockedSlots);
    } catch (error) {
      console.error('Erreur chargement créneaux:', error);
      setBlockedSlots([]);
    }
  };

  useEffect(() => {
    if (bookingData.date) {
      fetchBlockedSlots(bookingData.date);
    }
  }, [bookingData.date]);

  const getDurationInMinutes = (duration) => {
    if (!duration) return 0;

    const hMatch = duration.match(/(\d+)h(\d+)?/);
    if (hMatch) {
      return parseInt(hMatch[1]) * 60 + (hMatch[2] ? parseInt(hMatch[2]) : 0);
    }

    const minMatch = duration.match(/(\d+)\s*mins?/);
    if (minMatch) {
      return parseInt(minMatch[1]);
    }

    return 0;
  };

  const timeToMinutes = (time) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  const getTimeSlotsForDate = (date) => {
    if (!date) return [];

    const day = new Date(date).getDay();
    const hours = openingHours[day];
    if (!hours) return [];

    const serviceDuration = getDurationInMinutes(selectedOption?.duration || service.duration);
    const totalDuration = serviceDuration + 15;

    const closingMinutes = timeToMinutes(hours.end);

    return allTimeSlots.filter((slot) => {
      const slotMinutes = timeToMinutes(slot);
      if (slot < hours.start) return false;
      return slotMinutes + totalDuration <= closingMinutes;
    });
  };

  useEffect(() => {
    if (bookingData.time) {
      setBookingData((prev) => ({
        ...prev,
        time: ''
      }));
    }
  }, [selectedOption]);

  const dailyTimeSlots = getTimeSlotsForDate(bookingData.date);
  const availableTimeSlots = dailyTimeSlots.filter(
    (slot) => !blockedSlots.includes(slot)
  );

  const sendConfirmationEmail = async (bookingInfo) => {
    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingInfo)
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Erreur API send-email:', data);
        return false;
      }

      return data.success;
    } catch (error) {
      console.error('Erreur envoi email:', error);
      return false;
    }
  };

  function extractAmount(priceStr) {
    const match = priceStr.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const handleBooking = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (!bookingData.date || !bookingData.time) {
      warning('Veuillez sélectionner une date et un créneau');
      return;
    }

    if (!selectedOption) {
      warning('Veuillez sélectionner une option de service');
      return;
    }

    setLoading(true);

    try {
      const newBlockedSlots = calculateBlockedSlots(bookingData.time, selectedOption.duration);

      await fetchBlockedSlots(bookingData.date);
      const isConflict = newBlockedSlots.some(slot => blockedSlots.includes(slot));

      if (isConflict) {
        error('Ce créneau n\'est plus disponible. Veuillez en choisir un autre.');
        setLoading(false);
        return;
      }

      const paymentActive = isPaymentEnabled();

      if (paymentActive) {
        const bookingRef = await addDoc(collection(db, 'bookings'), {
          userId: user.uid,
          userName: userData?.name || user.email,
          userEmail: user.email,
          userPhone: userData?.phone || '',
          serviceName: service.name,
          serviceDuration: selectedOption.duration,
          servicePrice: selectedOption.promotion?.hasPromotion ? selectedOption.promotion.discountedPrice : selectedOption.price,
          date: bookingData.date,
          timeSlot: bookingData.time,
          blockedSlots: newBlockedSlots,
          createdAt: new Date().toISOString(),
          status: 'pending_payment',
          paymentStatus: 'pending',
          paymentIntentId: null
        });

        setPendingBookingId(bookingRef.id);
        setLoading(false);

        setShowCheckoutForm(true);

      } else {
        await addDoc(collection(db, 'bookings'), {
          userId: user.uid,
          userName: userData?.name || user.email,
          userEmail: user.email,
          userPhone: userData?.phone || '',
          serviceName: service.name,
          serviceDuration: selectedOption.duration,
          servicePrice: selectedOption.promotion?.hasPromotion ? selectedOption.promotion.discountedPrice : selectedOption.price,
          date: bookingData.date,
          timeSlot: bookingData.time,
          blockedSlots: newBlockedSlots,
          createdAt: new Date().toISOString(),
          status: 'confirmed',
          paymentStatus: 'pay_on_site',
          paymentType: 'on_site',
          paymentIntentId: null,
          note: 'Réservation sans paiement en ligne - Paiement sur place'
        });

        const finalPrice = selectedOption.promotion?.hasPromotion ? selectedOption.promotion.discountedPrice : selectedOption.price;
        const finalPriceAmount = extractAmount(finalPrice);

        await sendConfirmationEmail({
          email: user.email,
          name: userData?.name || user.email,
          service: service.name,
          date: bookingData.date,
          time: bookingData.time,
          duration: selectedOption.duration,
          price: finalPrice,
          paymentType: 'on_site',
          amountPaid: 0,
          remainingAmount: finalPriceAmount
        });

        setLoading(false);

        success(`Réservation confirmée !\n\n📧 Un email de confirmation a été envoyé à ${user.email}\n\n${service.name}\n📅 ${new Date(bookingData.date).toLocaleDateString('fr-FR')}\n🕐 ${bookingData.time}\n\n💵 Paiement sur place : ${finalPrice}\n\n⚠️ Les paiements en ligne seront disponibles à partir du 9 février.\n\nÀ bientôt ! ✨`);

        setShowBooking(false);
        setBookingData({ date: '', time: '' });
      }
    } catch (error) {
      console.error('Erreur:', error);
      error('Erreur lors de la réservation. Veuillez réessayer.');
      setLoading(false);
    }
  };

  const selectedPromotion = selectedOption?.promotion || applyPromotion(service.price, category?.title);
  const finalPrice = selectedPromotion.hasPromotion
    ? selectedPromotion.discountedPrice
    : (selectedOption?.price || service.price);

  const finalPriceAmount = extractAmount(finalPrice);

  const handlePaymentSuccess = async (paymentIntentId, paymentType, amountPaid) => {
    try {
      const paymentStatus = paymentType === 'full' ? 'captured' : 'authorized';

      await updateDoc(doc(db, 'bookings', pendingBookingId), {
        status: 'confirmed',
        paymentStatus: paymentStatus,
        paymentType: paymentType,
        amountPaid: amountPaid,
        paymentIntentId: paymentIntentId,
        confirmedAt: new Date().toISOString()
      });

      const fullAmount = extractAmount(selectedOption.price);
      const depositAmount = Math.round(fullAmount * 0.20 * 100) / 100;
      const remainingAmount = paymentType === 'full' ? 0 : (fullAmount - depositAmount).toFixed(2);

      await sendConfirmationEmail({
        email: user.email,
        name: userData?.name || user.email,
        service: service.name,
        date: bookingData.date,
        time: bookingData.time,
        duration: selectedOption.duration,
        price: selectedOption.price,
        paymentType: paymentType,
        amountPaid: amountPaid,
        remainingAmount: remainingAmount
      });

      setShowCheckoutForm(false);

      const paymentMessage = paymentType === 'full'
        ? `Paiement complet de ${amountPaid}€ effectué\n Prestation entièrement réglée`
        : `Accompte de ${amountPaid}€ payé\n Restant à régler sur place : ${remainingAmount}€`;

      success(`Réservation confirmée et paiement effectué !\n\n Un email de confirmation a été envoyé à ${user.email}\n\n${service.name}\n📅 ${new Date(bookingData.date).toLocaleDateString('fr-FR')}\n🕐 ${bookingData.time}\n\n${paymentMessage}\n\nÀ bientôt ! ✨`);

      setShowBooking(false);
      setBookingData({ date: '', time: '' });
      setPendingBookingId(null);

    } catch (error) {
      console.error('Erreur confirmation:', error);
      error('Erreur lors de la confirmation. Veuillez contacter le salon.');
    }
  };

  const handlePaymentCancel = async () => {
    if (pendingBookingId) {
      try {
        await updateDoc(doc(db, 'bookings', pendingBookingId), {
          status: 'cancelled',
          paymentStatus: 'cancelled'
        });
      } catch (error) {
        console.error('Erreur annulation:', error);
      }
    }
    setShowCheckoutForm(false);
    setPendingBookingId(null);
    setLoading(false);
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    setShowBooking(true);
  };

  return (
    <>
      <AlertModal
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText={alertConfig.confirmText}
        onConfirm={alertConfig.onConfirm}
        showCancel={alertConfig.showCancel}
        cancelText={alertConfig.cancelText}
      />
      <div className="max-w-6xl mx-auto">
        {showAuthModal && (
          <AuthModal
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={handleAuthSuccess}
          />
        )}

        {showCheckoutForm && (
          <CheckoutForm
            isOpen={showCheckoutForm}
            onClose={handlePaymentCancel}
            amount={finalPriceAmount}
            onPaymentSuccess={handlePaymentSuccess}
            serviceCategory={category?.title || 'Autre'}
            bookingDetails={{
              bookingId: pendingBookingId,
              userName: userData?.name || user?.email || '',
              userEmail: user?.email || '',
              serviceName: service.name,
              date: bookingData.date,
              time: bookingData.time
            }}
          />
        )}

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="flex flex-col md:flex-row min-h-[600px]">
            {/* Section gauche - Image */}
            <div className="w-full md:w-2/5 flex items-center justify-center overflow-hidden rounded-tl-2xl rounded-bl-2xl">
              {SERVICE_IMAGES[service.name] ? (
                <img
                  src={SERVICE_IMAGES[service.name]}
                  alt={service.name}
                  className="object-cover w-full h-full"
                  style={{ minHeight: '600px' }}
                />
              ) : (
                <div className='w-full h-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center p-8'>
                  <div className="text-center">
                    <div className="w-48 h-48 mx-auto mb-4 bg-white/50 rounded-full flex items-center justify-center">
                      <svg viewBox="0 0 100 100" className="w-32 h-32">
                        <path d="M50 20 L60 35 L75 35 L65 50 L70 65 L50 55 L30 65 L35 50 L25 35 L40 35 Z"
                          fill="none" stroke="#8B5CF6" strokeWidth="2" />
                        <circle cx="50" cy="50" r="5" fill="#8B5CF6" />
                      </svg>
                    </div>
                    <p className="text-purple-600 font-light text-sm">Image à venir</p>
                  </div>
                </div>
              )}
                </div>

            {/* Section droite - Détails */}
              <div className="w-full md:w-3/5 p-8 flex flex-col">
                <div className="flex-1">
                  {/* Titre du service */}
                  <h2 className="text-3xl font-light text-purple-900 mb-4">{service.name}</h2>

                  {/* Catégorie avec informations complémentaires */}
                  {category?.title && (
                    <div className="mb-6 bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                      <p className="text-lg text-purple-700 font-semibold flex items-center gap-2">
                        <Sparkles className="w-5 h-5" />
                        {category.title}
                      </p>

                      {/* Informations complémentaires de la catégorie */}
                      {category.info && (
                        <p className="text-sm text-gray-600 italic mt-2 pl-7">
                          ℹ️ {category.info}
                        </p>
                      )}

                      {/* Compléments disponibles */}
                      {category.complements && (
                        <p className="text-sm text-purple-600 font-medium mt-2 pl-7">
                          ✨ Compléments disponibles : {category.complements}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Badge de promotion si applicable */}
                  {selectedPromotion.hasPromotion && (
                    <div className="mb-6 bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border-2 border-red-400">
                      <div className="flex items-center gap-3">
                        <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-2 rounded-full shadow-lg">
                          <span className="text-sm font-bold flex items-center gap-2">
                            <Tag className="w-4 h-4" />
                            -{selectedPromotion.discountPercent}%
                          </span>
                        </div>
                        <div>
                          <p className="text-red-700 font-bold">Promotion en cours !</p>
                          {selectedPromotion.promotionName && (
                            <p className="text-sm text-gray-600 italic">
                              {selectedPromotion.promotionName}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Options de durée/prix et infos supp */}
                  {serviceOptions.length > 1 ? (
                    <div className="mb-6">
                      <p className="text-sm font-medium text-gray-700 mb-3">Choisissez votre formule :</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {serviceOptions.map((option, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedOption(option)}
                            className={`p-4 rounded-lg border-2 transition-all text-left ${selectedOption === option
                              ? 'border-purple-600 bg-purple-50 shadow-lg'
                              : 'border-gray-200 hover:border-purple-300'
                              }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-purple-600" />
                                <span className="font-semibold text-purple-900">{option.duration}</span>
                              </div>

                              <PriceDisplay
                                price={option.price}
                                categoryName={category?.title}
                                promotion={option.promotion}
                                className="text-sm"
                              />
                            </div>

                            {option.description && (
                              <p className="text-sm text-gray-600 italic">{option.description}</p>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-6 text-gray-600 mb-6 pb-6 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-purple-600" />
                        <span className="font-medium">{service.duration}</span>
                      </div>
                      <PriceDisplay
                        price={service.price}
                        categoryName={category?.title}
                        promotion={applyPromotion(service.price, category?.title)}
                        className="text-2xl font-semibold"
                      />
                    </div>
                  )}

                  <p className="text-gray-700 text-lg leading-relaxed mb-8">{service.description}</p>
                </div>

                {isEclipseaService ? (
                  /* Affichage spécial pour Eclipse autour du monde */
                  <div className="space-y-4">
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                      <p className="text-sm text-blue-900 font-semibold mb-2">
                        ℹ️ Réservation via Eclipsea
                      </p>
                      <p className="text-sm text-blue-800">
                        Cette prestation inclut un accès au spa privatif Eclipsea.
                        Pour réserver, rendez-vous directement sur le site d'Eclipsea.
                      </p>
                    </div>

                    <a
                      href="https://www.eclipsea.fr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-4 rounded-lg font-medium hover:from-blue-700 hover:to-cyan-700 transition-colors flex items-center justify-center gap-2 shadow-lg"
                    >
                      <Calendar className="w-5 h-5" />
                      Réserver sur www.eclipsea.fr
                    </a>
                  </div>
                ) : (
                  /* Système de réservation normal */
                  <>

                    {!user && (
                      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                        <p className="text-sm text-amber-800">
                          <LogIn className="w-4 h-4 inline mr-2" />
                          Connectez-vous ou créez un compte pour réserver
                        </p>
                      </div>
                    )}

                    {selectedOption && (
                      <>
                        {isPaymentEnabled() ? (
                          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                            <p className="text-sm text-blue-800">
                              <strong>Paiement sécurisé :</strong> Vous pourrez choisir de payer :
                            </p>
                            <ul className="text-sm text-blue-800 ml-4 mt-2 space-y-1">
                              <li>• <strong>Accompte de {Math.round(finalPriceAmount * 0.20 * 100) / 100}€</strong> (20% du montant) pour confirmer votre réservation</li>
                              <li>• <strong>Paiement complet de {finalPriceAmount}€</strong> pour régler la totalité maintenant</li>
                            </ul>
                          </div>
                        ) : (
                          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                            <p className="text-sm text-amber-800 font-semibold mb-2">
                              Paiements en ligne temporairement indisponibles
                            </p>
                            <p className="text-sm text-amber-800">
                              Les paiements en ligne seront disponibles à partir du <strong>9 février 2026</strong>.
                            </p>
                            <p className="text-sm text-amber-800 mt-2">
                              Pour le moment, vous pouvez réserver votre créneau et <strong>payer sur place</strong> le jour de votre rendez-vous.
                            </p>
                            <p className="text-sm text-amber-700 mt-2 font-medium">
                              Montant à régler sur place : {finalPrice}
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    {!showBooking ? (
                      <button
                        onClick={() => (user ? setShowBooking(true) : setShowAuthModal(true))}
                        className="w-full bg-purple-600 text-white py-4 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <Calendar className="w-5 h-5" />
                        Réserver ce soin
                      </button>
                    ) : (
                      <div className="bg-purple-50 p-6 rounded-lg">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-xl font-medium text-purple-900">Réservation</h3>
                          <button
                            onClick={() => setShowBooking(false)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <X className="w-6 h-6" />
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div className="bg-white p-3 rounded-lg border border-purple-200">
                            <p className="text-sm text-gray-600">Réservation pour :</p>
                            <p className="font-medium text-purple-900">{userData?.name || user.email}</p>
                          </div>

                          {selectedOption && (
                            <div className="bg-white p-3 rounded-lg border border-purple-200">
                              <p className="text-sm text-gray-600">Formule sélectionnée :</p>
                              <p className="font-medium text-purple-900">{selectedOption.duration} - {selectedOption.promotion?.hasPromotion ? selectedOption.promotion.discountedPrice : selectedOption.price}</p>
                              {selectedOption.description && (
                                <p className="text-xs text-gray-500 italic">{selectedOption.description}</p>
                              )}
                            </div>
                          )}

                          <input
                            type="date"
                            value={bookingData.date}
                            onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:border-purple-500 focus:outline-none"
                          />

                          <div>
                            <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                              <Clock className="w-4 h-4 text-purple-600" />
                              Choisissez un créneau
                            </p>

                            {!bookingData.date ? (
                              <p className="text-gray-400 text-sm italic">
                                Sélectionnez d'abord une date
                              </p>
                            ) : (
                              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                {availableTimeSlots.map((time) => {
                                  const isSelected = bookingData.time === time;

                                  return (
                                    <button
                                      key={time}
                                      type="button"
                                      onClick={() =>
                                        setBookingData({ ...bookingData, time })
                                      }
                                      className={`
                                px-3 py-2 rounded-lg text-sm font-medium transition-all
                                ${isSelected
                                          ? 'bg-purple-600 text-white shadow-md'
                                          : 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-100'
                                        }
                              `}
                                    >
                                      {time}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {bookingData.date && availableTimeSlots.length === 0 && (
                            <p className="text-red-600 text-sm mt-2">Aucun créneau disponible pour cette date (jour fermé ou complet)</p>
                          )}

                          <p className="text-xs text-gray-500 italic">
                            Les créneaux affichés tiennent compte de la durée du soin
                          </p>

                          <button
                            onClick={handleBooking}
                            disabled={loading}
                            className={`w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                          >
                            {loading ? ('Réservation en cours') : isPaymentEnabled() ? ('Continuer vers le paiement') : ('✓ Confirmer la réservation (paiement sur place)')}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button onClick={handleBackToSite} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors">
              Retour aux catégories
            </button>
          </div>
        </div>
      </>
      );
};

      export default ServiceDetail;